import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as UserSessionActions from './user-session.actions';
import { UserSessionService } from '../../http/user-session.service';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { Router } from '@angular/router';
import {
  TwoFactorTypes,
  UserProfileModel,
} from '../../models/user-profile.model';
import { environment } from '../../../../environments/environment';
import { MessageService } from '../../services/message.service';
import { TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngrx/store';
import { getSession } from './user-session.actions';
import { promptMfa } from '../mfa/mfa.actions';
import { RootState } from '../shared/root-state.interface';
import { selectMfaOriginUrl } from '../mfa/mfa.selectors';
import * as MfaActions from '../mfa/mfa.actions';
import { MfaErrors } from '../mfa/mfa.state';

export const loginEffect = createEffect(
  (
    actions$ = inject(Actions),
    router = inject(Router),
    userSessionService = inject(UserSessionService),
  ) =>
    actions$.pipe(
      ofType(UserSessionActions.login),
      switchMap((action) =>
        userSessionService.login(action.username, action.password).pipe(
          map((userData) => {
            if (userData.twoFactorDelay) {
              router.navigate(['/home']);
              return getSession();
            }
            if (userData.twoFactorEnabled) {
              router.navigate(['/two-factor']);
            } else {
              router.navigate(['/set-up-two-factor']);
            }
            return promptMfa({
              originProduct: { ...userData.originProduct, code: 'UP' },
              originUrl: null,
              twoFactorType: userData.twoFactorType as TwoFactorTypes,
            });
          }),
          catchError((error) => {
            if (error.status === 417) {
              return of(
                MfaActions.mfaError({
                  errorType: MfaErrors.IS_EMAIL_MISSING,
                }),
              );
            } else {
              return of(UserSessionActions.loginError());
            }
          }),
        ),
      ),
    ),
  { functional: true },
);

export const logoutEffect = createEffect(
  (
    actions$ = inject(Actions),
    router = inject(Router),
    userSessionService = inject(UserSessionService),
    store = inject(Store<RootState>),
  ) =>
    actions$.pipe(
      ofType(UserSessionActions.logout),
      withLatestFrom(store.pipe(select(selectMfaOriginUrl))),
      switchMap(([action, originUrl]) =>
        userSessionService.logout().pipe(
          map(() => {
            if (originUrl && action.isTimedOut) {
              window.open(originUrl, '_self');
            }
            router.navigate(['/login']);
            return UserSessionActions.logoutSuccess({
              isTimedOut: action.isTimedOut,
            });
          }),
          catchError(() => of(UserSessionActions.logoutError())),
        ),
      ),
    ),
  { functional: true },
);

export const getSessionEffect = createEffect(
  (
    actions$ = inject(Actions),
    userSessionService = inject(UserSessionService),
  ) =>
    actions$.pipe(
      ofType(UserSessionActions.getSession),
      switchMap(() =>
        userSessionService.getUser().pipe(
          map((userData: UserProfileModel) =>
            UserSessionActions.getSessionSuccess({ userData }),
          ),
          catchError(() => of(UserSessionActions.getSessionError())),
        ),
      ),
    ),
  { functional: true },
);

export const createUserEffect = createEffect(
  (
    actions$ = inject(Actions),
    userSessionService = inject(UserSessionService),
  ) =>
    actions$.pipe(
      ofType(UserSessionActions.createUser),
      switchMap((action) =>
        userSessionService.createUser(action.userData, action.token).pipe(
          map(() => UserSessionActions.completePairingSuccess()),
          catchError((res) =>
            of(
              UserSessionActions.createUserError({
                errors: { ...res.error, status: res.status },
              }),
            ),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const authenticateAndPairEffect = createEffect(
  (
    actions$ = inject(Actions),
    userSessionService = inject(UserSessionService),
  ) =>
    actions$.pipe(
      ofType(UserSessionActions.authenticateAndPair),
      switchMap((action) =>
        userSessionService.login(action.username, action.password).pipe(
          switchMap(() => {
            return userSessionService.completePairing(action.token).pipe(
              map(() => UserSessionActions.completePairingSuccess()),
              catchError(() => of(UserSessionActions.completePairingError())),
            );
          }),
          catchError(() => of(UserSessionActions.loginError())),
        ),
      ),
    ),
  { functional: true },
);

export const completePairingSuccessEffect = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) =>
    actions$.pipe(
      ofType(UserSessionActions.completePairingSuccess),
      switchMap(() => {
        router.navigate(['/home']);
        return of(UserSessionActions.getSession());
      }),
    ),
  { functional: true },
);

export const pairProductEffect = createEffect(
  (
    actions$ = inject(Actions),
    userSessionService = inject(UserSessionService),
    messageService = inject(MessageService),
    translateService = inject(TranslateService),
    store = inject(Store),
  ) =>
    actions$.pipe(
      ofType(UserSessionActions.pairProduct),
      switchMap((action) =>
        userSessionService.pairProduct().pipe(
          map((response) => {
            window.open(
              environment.productUrls[action.productCode] +
                '?token=' +
                response.token +
                '&type=' +
                response.type,
              '_blank',
            );
            messageService.showSuccess({
              title: translateService.instant('home.linkAccount'),
              text: translateService.instant('pairing.linkAccountText', {
                actablUnifiedAccount: translateService.instant(
                  'actablUnifiedAccount',
                ),
              }),
              buttons: [
                {
                  label: translateService.instant('cancel'),
                  type: 'cancel',
                },
                {
                  label: translateService.instant('pairing.linkAccountButton'),
                  type: 'confirm',
                  actionFn: () => {
                    store.dispatch(getSession());
                  },
                },
              ],
            });

            return UserSessionActions.pairProductSuccess();
          }),
          catchError(() => of(UserSessionActions.pairProductError())),
        ),
      ),
    ),
  { functional: true },
);

export const unpairProductEffect = createEffect(
  (
    actions$ = inject(Actions),
    userSessionService = inject(UserSessionService),
  ) =>
    actions$.pipe(
      ofType(UserSessionActions.unpairProduct),
      switchMap((action) =>
        userSessionService.unpairProduct(action.product).pipe(
          map(() =>
            UserSessionActions.unpairProductSuccess({
              productCode: action.product.code,
            }),
          ),
          catchError(() => of(UserSessionActions.unpairProductError())),
        ),
      ),
    ),
  { functional: true },
);

export const switchToProductEffect = createEffect(
  (
    actions$ = inject(Actions),
    userSessionService = inject(UserSessionService),
    router = inject(Router),
  ) =>
    actions$.pipe(
      ofType(UserSessionActions.switchToProduct),
      switchMap((action) =>
        userSessionService.getSwitchToken(action.product).pipe(
          map((response) => {
            const separator = response.loginUrl.includes('?') ? '&' : '?';
            const enforceHttps = response.loginUrl.includes('http')
              ? ''
              : 'https://';
            window.open(
              enforceHttps +
                response.loginUrl +
                action.product.pathParam +
                separator +
                action.product.queryParam +
                response.token +
                '&type=' +
                response.type,
              action.isRedirect ? '_self' : '_blank',
            );
            return UserSessionActions.switchToProductSuccess();
          }),
          catchError(() => {
            router.navigate(['/home']);
            return of(getSession());
          }),
        ),
      ),
    ),
  { functional: true },
);
